// ═══════════════════════════════════════════
// MonArgent Pro — Main App Component
// Root controller: navigation + global state
// ═══════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import { save, load, clearAll, saveTransaction, saveConfig, getConfig } from "./utils/database";
import { requestPermission } from "./utils/notifications";
import { THEME } from "./utils/helpers";

// ─── Screens (lazy-loaded for performance) ───
import Splash from "./components/Splash";
import Onboarding from "./components/Onboarding";
import Home from "./components/Home";
import Chat from "./components/Chat";
import PinLock from "./components/PinLock";
import Dashboard from "./components/Dashboard";

export default function App() {
  const [screen, setScreen] = useState("loading");
  const [config, setConfig] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);

  // ─── Initialize: load saved data ───
  useEffect(() => {
    async function init() {
      try {
        const cfg = await getConfig();
        const txns = await load("transactions", []);
        if (cfg?.setupDone) {
          setConfig(cfg);
          setTransactions(txns);
        }
        // Small delay for splash
        setTimeout(() => setScreen("splash"), 100);
      } catch (err) {
        console.error("[App] Init error:", err);
        setScreen("splash");
      }
    }
    init();
  }, []);

  // ─── Request notification permission on first load ───
  useEffect(() => {
    if (config?.setupDone) {
      requestPermission();
    }
  }, [config]);

  // ─── Listen for Electron navigation commands ───
  useEffect(() => {
    if (window.electronAPI?.onNavigate) {
      window.electronAPI.onNavigate((route) => {
        if (route === "dashboard") setScreen("pin");
        if (route === "chat") setScreen("home");
      });
    }
  }, []);

  // ─── Handlers ───
  const handleSplashDone = useCallback(() => {
    setScreen(config?.setupDone ? "home" : "onboarding");
  }, [config]);

  const handleSetupComplete = useCallback(async (cfg) => {
    const fullConfig = { ...cfg, setupDone: true, createdAt: new Date().toISOString() };
    setConfig(fullConfig);
    await saveConfig(fullConfig);
    setScreen("home");
  }, []);

  const handleNavigate = useCallback((target, data) => {
    switch (target) {
      case "chat":
        setCurrentEmployee(data || null);
        setScreen("chat");
        break;
      case "pin":
        setScreen("pin");
        break;
      case "dashboard":
        setScreen("dashboard");
        break;
      case "home":
        setScreen("home");
        break;
      default:
        setScreen("home");
    }
  }, []);

  const handlePinSuccess = useCallback(() => {
    setScreen("dashboard");
  }, []);

  const handleNewTransaction = useCallback(async (txn) => {
    const updatedTxns = await saveTransaction(txn);
    setTransactions(updatedTxns);
  }, []);

  const handleReset = useCallback(async () => {
    await clearAll();
    setConfig(null);
    setTransactions([]);
    setScreen("onboarding");
  }, []);

  // ─── Render ───
  return (
    <div style={{
      fontFamily: THEME.font,
      maxWidth: 480,
      margin: "0 auto",
      minHeight: "100vh",
      background: THEME.bg,
      position: "relative",
      overflow: "hidden",
    }}>
      <link
        href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800;900&display=swap"
        rel="stylesheet"
      />
      <GlobalStyles />

      {screen === "loading" && <LoadingScreen />}
      {screen === "splash" && <Splash onDone={handleSplashDone} />}
      {screen === "onboarding" && <Onboarding onComplete={handleSetupComplete} />}
      {screen === "home" && config && (
        <Home config={config} transactions={transactions} onNavigate={handleNavigate} />
      )}
      {screen === "chat" && config && (
        <Chat
          config={config}
          employee={currentEmployee}
          transactions={transactions}
          onTransaction={handleNewTransaction}
          onBack={() => setScreen("home")}
        />
      )}
      {screen === "pin" && config && (
        <PinLock
          correctPin={config.pin}
          onSuccess={handlePinSuccess}
          onBack={() => setScreen("home")}
          color={config.businessType}
        />
      )}
      {screen === "dashboard" && config && (
        <Dashboard
          config={config}
          transactions={transactions}
          onBack={() => setScreen("home")}
          onReset={handleReset}
        />
      )}
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{
      height: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", background: "#111",
    }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, animation: "pulse 2s ease-in-out infinite" }}>💰</div>
        <div style={{ color: "#fff", fontSize: 18, fontWeight: 900, marginTop: 12, fontFamily: THEME.font }}>MonArgent</div>
      </div>
    </div>
  );
}

function GlobalStyles() {
  return (
    <style>{`
      @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
      @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      @keyframes fadeIn { from{opacity:0} to{opacity:1} }
      @keyframes popIn { from{transform:scale(0) rotate(-180deg)} to{transform:scale(1) rotate(0deg)} }
      @keyframes bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-5px)} }
      @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)} 60%{transform:translateX(-6px)} 80%{transform:translateX(4px)} }
      @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.1)} }
      @keyframes slideIn { from{opacity:0;transform:translateX(30px)} to{opacity:1;transform:translateX(0)} }
      @keyframes slideDown { from{opacity:0;transform:translateY(-20px)} to{opacity:1;transform:translateY(0)} }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      ::-webkit-scrollbar { width: 3px; height: 3px; }
      ::-webkit-scrollbar-thumb { background: #ddd; border-radius: 3px; }
      input::placeholder { color: #bbb; }
      button { font-family: 'Nunito', sans-serif; }
      button:active { transform: scale(0.97); }
    `}</style>
  );
}
